import { Box, Flex, Heading, Icon, Text } from '@chakra-ui/react';
import { MdAdd } from 'react-icons/md';
import type { RecipeCollection } from '@/types/api';
import { useRecipesByIds } from '@/lib/recipeService';
import { getCollectionInitials, getCollectionColour } from '@/lib/collectionUtils';

interface CollectionTileProps {
  collection: RecipeCollection;
  onClick: () => void;
  height?: string;
  showPreview?: boolean;
}

export function CollectionTile({
  collection,
  onClick,
  height = '90px',
  showPreview = false,
}: CollectionTileProps) {
  const recipeIds = collection.recipeIds ?? [];
  const count = recipeIds.length;
  const { recipes: resolvedRecipes } = useRecipesByIds(recipeIds);
  const previewNames = showPreview ? resolvedRecipes.slice(0, 2).map((r) => r.title) : [];

  const initials = getCollectionInitials(collection.title);
  const bg = getCollectionColour(collection.id);

  return (
    <Box
      borderRadius="12px"
      overflow="hidden"
      position="relative"
      h={height}
      cursor="pointer"
      onClick={onClick}
      _hover={{ opacity: 0.9 }}
      transition="opacity 0.15s"
      style={{ background: bg }}
    >
      {/* Initials watermark */}
      <Box
        position="absolute"
        inset={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        pointerEvents="none"
        aria-hidden
      >
        <Text
          fontFamily="heading"
          fontWeight="800"
          fontSize="3xl"
          color="white"
          userSelect="none"
          style={{ opacity: 0.15 }}
        >
          {initials}
        </Text>
      </Box>

      {/* Bottom gradient so text is readable */}
      <Box
        position="absolute"
        inset={0}
        background="linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.35) 45%, rgba(0,0,0,0.88) 100%)"
        pointerEvents="none"
      />

      <Flex direction="column" justify="flex-end" h="100%" p={2.5} position="relative" zIndex={1}>
        <Heading
          fontFamily="heading"
          fontWeight="800"
          fontSize="15px"
          color="white"
          letterSpacing="-0.2px"
          lineHeight="1.2"
          lineClamp={2}
          style={{ textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}
        >
          {collection.title}
        </Heading>
        <Text fontFamily="body" fontWeight="500" fontSize="12px" color="white" mt={0.5} style={{ textShadow: '0 1px 3px rgba(0,0,0,0.7)', opacity: 0.9 }}>
          {count} {count === 1 ? 'recipe' : 'recipes'}
        </Text>
        {previewNames.length > 0 && (
          <Flex direction="column" gap={0} mt={1}>
            {previewNames.map(name => (
              <Text
                key={name}
                fontFamily="body"
                fontWeight="400"
                fontSize="10px"
                color="whiteAlpha.700"
                lineClamp={1}
              >
                {name}
              </Text>
            ))}
          </Flex>
        )}
      </Flex>
    </Box>
  );
}

export function AddCollectionTile({
  onClick,
  height = '90px',
}: {
  onClick: () => void;
  height?: string;
}) {
  return (
    <Box
      borderRadius="12px"
      h={height}
      bg="bg.surface"
      border="1.5px dashed"
      borderColor="border.muted"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={1}
      cursor="pointer"
      onClick={onClick}
      _hover={{ bg: 'bg.muted' }}
      transition="background 0.15s"
    >
      <Icon as={MdAdd} boxSize={4} color="text.muted" />
      <Text fontFamily="body" fontWeight="500" fontSize="10px" color="text.muted">
        New collection
      </Text>
    </Box>
  );
}
